import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { Plus, Users, Search, X, Tag, Download, Upload, Printer, Mail, MapPin, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { adminAPI, clientCategoriesAPI, csvAPI } from '../lib/api';
import api from '../lib/api';
import { toast } from 'sonner';
import { printContent, generateClientsListHTML, generateClientCardHTML } from '../lib/print';
import { ClientTable } from '../components/admin/clients/ClientTable';
import { ClientForm } from '../components/admin/clients/ClientForm';

const MapView = lazy(() => import('../components/MapView').then(module => ({ default: module.default })));
const CLIENTS_PER_PAGE = 50;

export const AdminClients = () => {
    const fileInputRef = useRef(null);
    const [clients, setClients] = useState([]);
    const [clientCategories, setClientCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('company_name');
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalClients, setTotalClients] = useState(0);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [clientToDelete, setClientToDelete] = useState(null);
    const [editingClient, setEditingClient] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
    const [selectedClients, setSelectedClients] = useState([]);
    const [sendingEmails, setSendingEmails] = useState(false);
    const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [showMap, setShowMap] = useState(false);
    const [mapClients, setMapClients] = useState([]);
    const [geocoding, setGeocoding] = useState(false);
    const [formData, setFormData] = useState({
        email: '', password: '', company_name: '', contact_name: '',
        phone: '', address: '', siret: '', client_category: 'autre', client_code: '',
    });

    useEffect(() => { loadClients(); loadClientCategories(); }, [currentPage, sortBy, statusFilter, searchQuery]);
    useEffect(() => {
        const timer = setTimeout(() => { setSearchQuery(search); setCurrentPage(1); }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    const loadClients = async () => {
        if (clients.length === 0) setLoading(true);
        try {
            const skip = (currentPage - 1) * CLIENTS_PER_PAGE;
            const params = { skip, limit: CLIENTS_PER_PAGE, sort: sortBy };
            if (searchQuery) params.search = searchQuery;
            if (statusFilter !== 'all') params.status = statusFilter;
            const res = await adminAPI.getClients(params);
            setClients(res.data.clients || []); setTotalClients(res.data.total || 0);
        } catch (error) { console.error('Error loading clients:', error); toast.error('Erreur lors du chargement des clients'); }
        finally { setLoading(false); }
    };

    const loadClientCategories = async () => {
        try { const res = await clientCategoriesAPI.getAll(); setClientCategories(res.data.categories || res.data || []); }
        catch (error) { console.error('Error loading client categories:', error); }
    };

    const handleAddCategory = async (e) => {
        e.preventDefault();
        if (!newCategoryName.trim()) { toast.error('Le nom de la categorie est requis'); return; }
        try { await clientCategoriesAPI.create({ name: newCategoryName.trim(), order: clientCategories.length + 1 }); toast.success('Categorie creee'); setNewCategoryName(''); setIsCategoryDialogOpen(false); loadClientCategories(); }
        catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la creation'); }
    };

    const handleDeleteCategory = async (categoryId) => {
        try { await clientCategoriesAPI.delete(categoryId); toast.success('Categorie supprimee'); loadClientCategories(); }
        catch { toast.error('Erreur lors de la suppression'); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.email || !formData.company_name || !formData.contact_name || !formData.phone || !formData.address) { toast.error('Veuillez remplir tous les champs obligatoires'); return; }
        if (!editingClient && !formData.password) { toast.error('Le mot de passe est obligatoire pour un nouveau client'); return; }
        try {
            if (editingClient) { await adminAPI.updateClient(editingClient.id, formData); toast.success('Client mis a jour'); }
            else { await adminAPI.createClient({ ...formData, send_email: sendWelcomeEmail }); toast.success(sendWelcomeEmail ? 'Client cree et email envoye' : 'Client cree'); }
            setIsDialogOpen(false); resetForm(); await loadClients();
        } catch (error) { console.error('Error submitting client:', error); toast.error(error?.response?.data?.detail || 'Erreur lors de l\'enregistrement'); }
    };

    const handleExportCSV = async () => {
        toast.info('Preparation du telechargement...');
        try { const result = await csvAPI.downloadClients(localStorage.getItem('prixdeal_token'));
            if (result.success) toast.success('Fichier telecharge !'); else toast.error(`Erreur: ${result.error || 'Echoue'}`);
        } catch (error) { console.error('Export error:', error); toast.error('Erreur lors du telechargement.'); }
    };

    const handlePrintList = () => {
        const filters = {}; if (search) filters.search = search;
        const success = printContent('Liste des Clients', generateClientsListHTML(clients, filters));
        if (success) toast.success('Fenetre d\'impression ouverte.'); else toast.error('Fenetre bloquee.');
    };

    const handlePrintClientCard = (client) => {
        const success = printContent(`Fiche Client - ${client.company_name}`, generateClientCardHTML(client));
        if (success) toast.success('Fenetre d\'impression ouverte'); else toast.error('Fenetre bloquee.');
    };

    const handleImportCSV = async (e) => {
        const file = e.target.files[0]; if (!file) return;
        const fd = new FormData(); fd.append('file', file);
        try {
            const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
            const res = isExcel ? await csvAPI.importClientsExcel(fd) : await csvAPI.importClients(fd);
            toast.success(`Import termine: ${res.data.imported} crees, ${res.data.updated} mis a jour`);
            if (res.data.errors?.length > 0) console.error('Import errors:', res.data.errors);
            loadClients();
        } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de l\'import'); }
        e.target.value = '';
    };

    const handleToggleSelectClient = (clientId) => { setSelectedClients(prev => prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]); };
    const handleSelectAll = () => { setSelectedClients(prev => prev.length === clients.length ? [] : clients.map(c => c.id)); };

    const handleSendWelcomeEmails = async () => {
        if (selectedClients.length === 0) { toast.error('Selectionnez au moins un client'); return; }
        setSendingEmails(true);
        try { const res = await adminAPI.sendWelcomeEmails(selectedClients); toast.success(`${res.data.sent} email(s) envoye(s)`);
            if (res.data.errors?.length > 0) res.data.errors.forEach(err => toast.error(err));
            setSelectedClients([]);
        } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi'); }
        finally { setSendingEmails(false); }
    };

    const handleEdit = (client) => {
        setEditingClient(client);
        setFormData({ email: client.email, password: '', company_name: client.company_name, contact_name: client.contact_name,
            phone: client.phone, address: client.address, siret: client.siret || '', client_category: client.client_category || 'autre', client_code: client.client_code || '' });
        setIsDialogOpen(true);
    };

    const handleToggleStatus = async (clientId) => {
        try { const res = await adminAPI.toggleClient(clientId); toast.success(res.data.is_active ? 'Client active' : 'Client desactive'); loadClients(); }
        catch { toast.error('Erreur lors de la mise a jour'); }
    };

    const handleDeleteClick = (client) => { setClientToDelete(client); setIsDeleteDialogOpen(true); };
    const handleDeleteConfirm = async () => {
        if (!clientToDelete) return;
        try { await adminAPI.deleteClient(clientToDelete.id); toast.success('Client supprime'); loadClients(); }
        catch { toast.error('Erreur lors de la suppression'); }
        finally { setIsDeleteDialogOpen(false); setClientToDelete(null); }
    };

    const handleShowClientsMap = async () => {
        try { const res = await api.get('/admin/clients/map');
            const markers = res.data.clients.map(c => ({ ...c, label: c.company_name }));
            setMapClients(markers); setShowMap(true);
            if (markers.length === 0) toast.info('Aucun client geocode.');
        } catch { toast.error('Erreur chargement carte'); }
    };

    const handleGeocode = async () => {
        setGeocoding(true);
        try { const res = await api.post('/admin/geocode/clients');
            toast.success(`${res.data.geocoded} clients geocodes. ${res.data.total_with_coords} au total.`);
            if (res.data.remaining > 0) toast.info('Relancez pour geocoder les suivants');
            handleShowClientsMap();
        } catch { toast.error('Erreur geocodage'); } finally { setGeocoding(false); }
    };

    const resetForm = () => {
        setEditingClient(null);
        setFormData({ email: '', password: '', company_name: '', contact_name: '', phone: '', address: '', siret: '', client_category: 'autre', client_code: '' });
    };

    const sendWhatsAppWelcome = null; // WhatsApp feature removed

    if (loading && clients.length === 0) {
        return (<div className="min-h-screen bg-sand flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Chargement...</div></div>);
    }

    return (
        <div className="min-h-screen bg-sand bg-botanical" data-testid="admin-clients-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-sage/20 flex items-center justify-center"><Users className="w-6 h-6 text-sage" /></div>
                        <div>
                            <h1 className="font-heading text-3xl font-bold tracking-tight text-jungle">Gestion des clients</h1>
                            <p className="text-muted-foreground">{totalClients} clients enregistres</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button onClick={handlePrintList} variant="outline" className="border-jungle/20 text-jungle rounded-xl" data-testid="print-clients-button"><Printer className="h-4 w-4 mr-2" />Imprimer</Button>
                        <Button onClick={handleExportCSV} variant="outline" className="border-jungle/20 text-jungle rounded-xl" data-testid="export-clients-button"><Download className="h-4 w-4 mr-2" />Exporter CSV</Button>
                        <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="border-jungle/20 text-jungle rounded-xl" data-testid="import-clients-button"><Upload className="h-4 w-4 mr-2" />Importer</Button>
                        <input type="file" ref={fileInputRef} accept=".csv,.xlsx,.xls" onChange={handleImportCSV} className="hidden" />
                        <Button onClick={() => setIsCategoryDialogOpen(true)} variant="outline" className="border-jungle/20 text-jungle rounded-xl" data-testid="manage-categories-button"><Tag className="h-4 w-4 mr-2" />Categories</Button>
                        <Button onClick={handleShowClientsMap} variant="outline" className="border-jungle/20 text-jungle rounded-xl" data-testid="show-clients-map-button"><MapPin className="h-4 w-4 mr-2" />Carte</Button>
                        {selectedClients.length > 0 && (
                            <Button onClick={handleSendWelcomeEmails} disabled={sendingEmails} className="bg-leaf hover:bg-leaf/90 text-white rounded-xl" data-testid="send-welcome-emails-button">
                                <Mail className="h-4 w-4 mr-2" />{sendingEmails ? 'Envoi...' : `Email bienvenue (${selectedClients.length})`}
                            </Button>
                        )}
                        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="bg-jungle hover:bg-jungle/90 text-sand rounded-xl" data-testid="add-client-button"><Plus className="h-4 w-4 mr-2" />Ajouter un client</Button>
                    </div>
                </div>

                {/* Map */}
                {showMap && (
                    <Card className="mb-6 border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl" data-testid="clients-map-card">
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-lg text-jungle flex items-center gap-2"><MapPin className="h-5 w-5" />Carte des clients ({mapClients.length} points)</CardTitle>
                                <div className="flex gap-2">
                                    <Button size="sm" variant="outline" className="rounded-xl" onClick={handleGeocode} disabled={geocoding} data-testid="geocode-btn">{geocoding ? 'Geocodage...' : 'Geocoder +20'}</Button>
                                    <Button size="sm" variant="ghost" onClick={() => setShowMap(false)}>Fermer</Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <Suspense fallback={<div className="h-96 bg-muted animate-pulse rounded-xl" />}><MapView markers={mapClients} height="400px" /></Suspense>
                        </CardContent>
                    </Card>
                )}

                {/* Search & Filters */}
                <Card className="mb-6 border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                    <CardContent className="pt-6">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="relative flex-1 flex gap-2">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input placeholder="Tapez pour rechercher (nom, code, SIRET, email...)" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white/80 border-jungle/20 rounded-xl" data-testid="search-input" />
                                </div>
                                {search && <Button type="button" variant="ghost" onClick={() => setSearch('')} className="text-destructive hover:bg-destructive/10 rounded-xl"><X className="h-4 w-4" /></Button>}
                            </div>
                            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
                                <SelectTrigger className="w-[150px] bg-white/80 border-jungle/20 rounded-xl" data-testid="status-filter"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value="all">Tous</SelectItem><SelectItem value="active">Actifs</SelectItem><SelectItem value="inactive">Inactifs</SelectItem></SelectContent>
                            </Select>
                            <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setCurrentPage(1); }}>
                                <SelectTrigger className="w-[180px] bg-white/80 border-jungle/20 rounded-xl" data-testid="sort-select"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value="company_name">A - Z (alphabetique)</SelectItem><SelectItem value="created_at">Plus recents</SelectItem></SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Clients Table */}
                <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                    <CardHeader>
                        <CardTitle className="font-heading text-jungle">
                            {totalClients} client{totalClients !== 1 ? 's' : ''} trouve{totalClients !== 1 ? 's' : ''}
                            {searchQuery && <span className="text-sm font-normal text-muted-foreground ml-2">pour "{searchQuery}"</span>}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ClientTable clients={clients} clientCategories={clientCategories} selectedClients={selectedClients}
                            handleSelectAll={handleSelectAll} handleToggleSelectClient={handleToggleSelectClient}
                            handleEdit={handleEdit} handleToggleStatus={handleToggleStatus} handleDeleteClick={handleDeleteClick}
                            handlePrintClientCard={handlePrintClientCard} />
                    </CardContent>
                    {totalClients > CLIENTS_PER_PAGE && (
                        <div className="flex items-center justify-between px-6 py-4 border-t border-jungle/10">
                            <span className="text-sm text-muted-foreground">Page {currentPage} / {Math.ceil(totalClients / CLIENTS_PER_PAGE)} ({totalClients} clients)</span>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1} className="rounded-xl border-jungle/20" data-testid="clients-prev-page">Precedent</Button>
                                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalClients / CLIENTS_PER_PAGE), p + 1))} disabled={currentPage >= Math.ceil(totalClients / CLIENTS_PER_PAGE)} className="rounded-xl border-jungle/20" data-testid="clients-next-page">Suivant</Button>
                            </div>
                        </div>
                    )}
                </Card>
            </div>

            <ClientForm isOpen={isDialogOpen} setIsOpen={setIsDialogOpen} editingClient={editingClient}
                formData={formData} setFormData={setFormData} clientCategories={clientCategories}
                sendWelcomeEmail={sendWelcomeEmail} setSendWelcomeEmail={setSendWelcomeEmail}
                showPassword={showPassword} setShowPassword={setShowPassword} onSubmit={handleSubmit} onReset={resetForm} />

            {/* Delete Dialog */}
            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent className="bg-sand"><AlertDialogHeader>
                    <AlertDialogTitle className="text-jungle">Confirmer la suppression</AlertDialogTitle>
                    <AlertDialogDescription>Etes-vous sur de vouloir supprimer le client "{clientToDelete?.company_name}" ? Cette action est irreversible.</AlertDialogDescription>
                </AlertDialogHeader><AlertDialogFooter>
                    <AlertDialogCancel className="rounded-xl">Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive hover:bg-destructive/90 rounded-xl">Supprimer</AlertDialogAction>
                </AlertDialogFooter></AlertDialogContent>
            </AlertDialog>

            {/* Category Dialog */}
            <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
                <DialogContent className="max-w-md bg-sand"><DialogHeader>
                    <DialogTitle className="font-heading text-jungle flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-leaf/10 flex items-center justify-center"><Tag className="w-4 h-4 text-leaf" /></div>
                        Categories de clients
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                    {clientCategories.map((cat) => (
                        <div key={cat.id} className="flex items-center justify-between p-3 bg-white/60 rounded-xl">
                            <span className="font-medium text-jungle">{cat.name}</span>
                            {cat.id !== 'autre' && <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => handleDeleteCategory(cat.id)}><Trash2 className="h-4 w-4" /></Button>}
                        </div>
                    ))}
                </div>
                <form onSubmit={handleAddCategory} className="flex gap-2 pt-4 border-t border-jungle/10">
                    <Input placeholder="Nouvelle categorie..." value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} className="border-jungle/20 rounded-xl" />
                    <Button type="submit" className="bg-jungle hover:bg-jungle/90 text-sand rounded-xl"><Plus className="h-4 w-4" /></Button>
                </form></DialogContent>
            </Dialog>
        </div>
    );
};
